import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { TabRailContext, type TabRailVariant } from "./useTabRailContext";
import { TabRailList } from "./TabRailList";
import { TabRailTabPart } from "./TabRailTab";

/**
 * A molecule composed as a namespaced compound component -- the row of tabs above a Collection's
 * board, plus whatever trailing controls the consumer chooses to place after it. The root only
 * owns selection state (shared context every `TabRail.Tab` needs) and the outer flex row; the
 * trailing controls are just ordinary elements the consumer supplies, so their icons, labels,
 * menus and actions stay the consumer's concern rather than TabRail's.
 *
 * @example
 * ```tsx
 * <TabRail activeTabId={id} onSelectTab={select}>
 *   <TabRail.List>
 *     {tabs.map((tab) => (
 *       <TabRail.Tab key={tab.id} value={tab.id} onDelete={() => remove(tab.id)} onRename={(label) => rename(tab.id, label)}>
 *         {tab.label}
 *       </TabRail.Tab>
 *     ))}
 *   </TabRail.List>
 *   <Button variant="ghost" color="muted" size="sm" iconOnly onClick={addTab} hoverLabel={t("New collection")}>
 *     <Plus className="h-3.5 w-3.5" />
 *   </Button>
 * </TabRail>
 *
 * // Route tabs in a page header rail: every tab is a link, so the rail renders as a `nav`.
 * <TabRail variant="inline" activeTabId={selectedTab}>
 *   <TabRail.List>
 *     <TabRail.Tab value="numbers" asChild>
 *       <Link to="/settings/phone/numbers">{t("Phone Numbers")}</Link>
 *     </TabRail.Tab>
 *   </TabRail.List>
 * </TabRail>
 * ```
 */
export interface TabRailProps {
  activeTabId: string;
  /** Called with a tab's `value` when it is selected. Optional for a rail of links, which navigates instead. */
  onSelectTab?: (id: string) => void;
  /**
   * `inline` is the page-header-rail look: the rail and its tabs draw no chrome (the header
   * provides the surface), tabs fill the rail's height, and the active tab is marked by its ink and
   * weight alone. `underline` is the `inline` look plus a bar under the active tab that fades in,
   * for section tabs inside a dialog or panel; give its container a height and bottom border.
   * @default "default"
   */
  variant?: TabRailVariant;
  className?: string;
  children: ReactNode;
}

const TabRailRoot = ({ activeTabId, onSelectTab, variant = "default", className, children }: TabRailProps) => (
  <TabRailContext.Provider value={{ activeTabId, onSelectTab, variant }}>
    <div data-slot="tab-rail" className={cn("flex items-center gap-1", variant !== "default" && "h-full", className)}>
      {children}
    </div>
  </TabRailContext.Provider>
);

export const TabRail = Object.assign(TabRailRoot, {
  List: TabRailList,
  Tab: TabRailTabPart,
});
